"use strict";

/**
 * An OrderrItemGroup reaches the pricing module in one of two shapes.
 *
 * A group the storefront just BUILT holds `orderrItems` as a plain array — nothing has been
 * saved yet. A group READ back from the API holds `orderrItems` as a GraphQL connection,
 * `{ items: [...] }`, because `OrderrItemGroup.orderrItems` is declared `@connection` in
 * `mobGraphQLAPI/schema.graphql`.
 *
 * Both shapes are legitimate. Neither belongs inside the pricing rules, so each caller
 * normalizes here first and `PriceCalculator` only ever sees plain arrays. This is the seam
 * that the two forked copies of `PriceCalculator` used to straddle: one assumed a built group,
 * the other a fetched one, and each was right about its own caller and wrong about the other's.
 *
 * Note that `priceTables` needs no normalization. It is `[PriceTable!]!` in the schema — a
 * plain list, never a connection, in either runtime.
 */
class OrderrItemGroupShape {
  /**
   * Return the group with `orderrItems` as a plain array, whichever shape it arrived in.
   * The input is not mutated. A null or undefined group is passed through unchanged, because
   * `PriceCalculator.calculatePriceOrderrItemGroup` owns that case.
   */
  static toPlain(orderrItemGroup) {
    if (orderrItemGroup == null) {
      return orderrItemGroup;
    }

    const items = OrderrItemGroupShape.itemsOf(orderrItemGroup);
    if (items === orderrItemGroup.orderrItems) {
      return orderrItemGroup;
    }

    return Object.assign({}, orderrItemGroup, { orderrItems: items });
  }

  /**
   * The group's OrderrItems as a plain array. Returns the original reference when the group is
   * already plain, so `toPlain` can avoid a copy it does not need.
   */
  static itemsOf(orderrItemGroup) {
    const orderrItems =
      orderrItemGroup == null ? null : orderrItemGroup.orderrItems;

    if (orderrItems == null) {
      return orderrItems;
    }
    if (Array.isArray(orderrItems)) {
      return orderrItems;
    }
    if (Array.isArray(orderrItems.items)) {
      return orderrItems.items;
    }
    return orderrItems;
  }
}

module.exports = OrderrItemGroupShape;
